import {
  type ReactNode,
  type ReactPortal,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"

import styles from "./Mask.module.css"

type RectStyles = {
  width: number
  height: number
  transform: string
  transitionProperty?: string
  transitionDuration?: string
}

const fadeInOutDuration = 200 // ms
const transitionDuration = 400 // ms

type MaskProps = {
  selector?: string | string[]
  show?: boolean
  padding?: number
  radius?: number
  children?: ReactNode
}

export function Mask(props: MaskProps): ReactPortal {
  const { selector, show = true, padding = 0, radius = 0, children } = props

  const [rectStyles, setRectStyles] = useState<RectStyles>()
  const isCleaningUpRect = useRef(false)
  const isTransitioningRect = useRef(false)
  const previousFrameElement = useRef(
    selector ? getFirstElement(selector) : undefined,
  )
  const maskId = useId()

  const container = document.getElementById("tour-mask-portal") ?? document.body

  useAnimationFrame(
    useCallback(onAnimationFrame, [rectStyles, padding, selector]),
  )

  function onAnimationFrame() {
    if (isTransitioningRect.current || isCleaningUpRect.current) return

    // If we transiton from showing the mask to not showing the mask,
    // remove the rect after the CSS fade completes.
    if (rectStyles && !selector) {
      isCleaningUpRect.current = true
      setTimeout(() => {
        setRectStyles(undefined)
        isCleaningUpRect.current = false
      }, fadeInOutDuration)
      return
    }

    if (!selector) return

    const element = getFirstElement(selector)
    if (!element) return

    const newRect = element.getBoundingClientRect()
    const final = {
      width: newRect.width + padding * 2,
      height: newRect.height + padding * 2,
      transform: `translate3d(${newRect.left - padding}px, ${
        newRect.top - padding
      }px, 0)`,
    }

    if (!rectStyles) {
      setRectStyles(final)
    } else if (previousFrameElement.current !== element) {
      isTransitioningRect.current = true
      setRectStyles({
        ...final,
        transitionProperty: "width, height, transform",
        transitionDuration: `${transitionDuration}ms`,
      })
      setTimeout(() => {
        setRectStyles(final)
        isTransitioningRect.current = false
      }, transitionDuration)
    } else if (
      rectStyles.width !== final.width ||
      rectStyles.height !== final.height ||
      rectStyles.transform !== final.transform
    ) {
      setRectStyles(final)
    }

    previousFrameElement.current = element
  }

  return createPortal(
    <div
      className={styles.root}
      style={{
        transitionDuration: `${fadeInOutDuration}ms`,
        opacity: show ? 1 : 0,
      }}
    >
      <svg width="100%" height="100%" className={styles.svg}>
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect x={0} y={0} fill="black" rx={radius} style={rectStyles} />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="var(--grey-alpha1)"
          mask={`url(#${maskId})`}
        />
      </svg>
      {children && rectStyles ? (
        <div className={styles.childrenRoot} style={rectStyles}>
          {children}
        </div>
      ) : null}
    </div>,
    container,
  )
}

function useAnimationFrame(callback: (time: number) => void) {
  const requestRef = useRef<number>()
  const previousTimeRef = useRef<number>()

  const animate = useCallback(
    (time: number) => {
      if (previousTimeRef.current != undefined) {
        const deltaTime = time - previousTimeRef.current
        callback(deltaTime)
      }
      previousTimeRef.current = time
      requestRef.current = requestAnimationFrame(animate)
    },
    [callback],
  )

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate)

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current)
    }
  }, [animate])
}

function getFirstElement(selectors: string | string[]) {
  for (const selector of [selectors].flat()) {
    const element = document.querySelector(selector)
    if (element) return element
  }
  return
}
